// Header component — full width, 100px tall.
// Contains: tenant logo/name (left), app name (centre), user menu (right).
// Breadcrumbs are in a separate bar below (see RootLayout).
//
// Auth-mode aware: OIDC tenants sign out via react-oidc-context signoutRedirect;
// magic link tenants sign out via POST /t/{slug}/auth/logout (server-side
// token revocation + cookie clear). useAuth() must NOT be called outside an
// AuthProvider — magic link tenants have no AuthProvider in the tree.

import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { LogOut, RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/components/shared/TenantShell'
import { useCurrentUserOrNull } from '@/lib/hooks/use-current-user'

interface HeaderProps {
  slug: string
  tenantName?: string
  logoUrl?: string
}

export function Header({ slug, tenantName, logoUrl }: HeaderProps) {
  const { authConfig } = useTenant()
  const isMagicLink = authConfig.auth_mode === 'magic_link'

  return isMagicLink ? (
    <MagicLinkHeader slug={slug} tenantName={tenantName} logoUrl={logoUrl} />
  ) : (
    <OIDCHeader slug={slug} tenantName={tenantName} logoUrl={logoUrl} />
  )
}

// ---------------------------------------------------------------------------
// OIDC header — uses react-oidc-context (only safe when AuthProvider is present)
// ---------------------------------------------------------------------------

function OIDCHeader({ slug, tenantName, logoUrl }: HeaderProps) {
  const { t } = useTranslation('layout')
  const auth = useAuth()
  const queryClient = useQueryClient()

  const userName = auth.user?.profile.name ?? auth.user?.profile.email ?? 'User'
  const userEmail = auth.user?.profile.email ?? ''
  const initials = getInitials(userName)

  return (
    <HeaderShell slug={slug} tenantName={tenantName} logoUrl={logoUrl}>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 text-header-foreground/70 hover:text-header-foreground"
        onClick={() => queryClient.invalidateQueries()}
      >
        <RefreshCw className="h-4 w-4" />
        <span className="hidden text-sm md:inline-block">Data Refresh</span>
      </Button>
      <UserMenu
        userName={userName}
        userEmail={userEmail}
        initials={initials}
        onSignOut={() => auth.signoutRedirect()}
        t={t}
      />
    </HeaderShell>
  )
}

// ---------------------------------------------------------------------------
// Magic link header — uses /me context for display name, POST logout for sign-out
// ---------------------------------------------------------------------------

function MagicLinkHeader({ slug, tenantName, logoUrl }: HeaderProps) {
  const { t } = useTranslation('layout')
  const me = useCurrentUserOrNull()
  const queryClient = useQueryClient()

  const userName = me?.display_name ?? me?.email ?? 'User'
  const userEmail = me?.email ?? ''
  const initials = getInitials(userName)

  async function handleSignOut() {
    try {
      await fetch(`/t/${slug}/auth/logout`, { method: 'POST', credentials: 'same-origin' })
    } finally {
      // Redirect to login page regardless of fetch outcome.
      // The server clears the cookie; the frontend clears query cache.
      queryClient.clear()
      window.location.href = `/t/${slug}/login`
    }
  }

  return (
    <HeaderShell slug={slug} tenantName={tenantName} logoUrl={logoUrl}>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 text-header-foreground/70 hover:text-header-foreground"
        onClick={() => queryClient.invalidateQueries()}
      >
        <RefreshCw className="h-4 w-4" />
        <span className="hidden text-sm md:inline-block">Data Refresh</span>
      </Button>
      <UserMenu
        userName={userName}
        userEmail={userEmail}
        initials={initials}
        onSignOut={handleSignOut}
        t={t}
      />
    </HeaderShell>
  )
}

// ---------------------------------------------------------------------------
// Shared layout shell — logo/name + centre title + right slot
// ---------------------------------------------------------------------------

interface HeaderShellProps {
  slug: string
  tenantName?: string
  logoUrl?: string
  children: React.ReactNode
}

function HeaderShell({ slug, tenantName, logoUrl, children }: HeaderShellProps) {
  const { t } = useTranslation('layout')

  return (
    <header className="relative flex h-[100px] items-center border-b bg-header px-6 text-header-foreground">
      {/* Left: tenant logo or name */}
      <div className="flex items-center">
        {logoUrl ? (
          <img src={logoUrl} alt={tenantName ?? slug} className="max-h-[76px] w-auto" />
        ) : (
          <span className="text-lg font-medium opacity-90">{tenantName ?? slug}</span>
        )}
      </div>

      {/* Centre: app name (absolute positioned for true centre) */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="text-[28px] font-bold tracking-tight">{t('header.appName')}</span>
      </div>

      {/* Right: caller-supplied controls (refresh + user menu) */}
      <div className="ml-auto flex items-center gap-1">{children}</div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Shared user dropdown menu
// ---------------------------------------------------------------------------

interface UserMenuProps {
  userName: string
  userEmail: string
  initials: string
  onSignOut: () => void
  t: (key: string) => string
}

function UserMenu({ userName, userEmail, initials, onSignOut, t }: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-sm">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden text-sm md:inline-block">{userName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{userName}</p>
          {userEmail && <p className="text-xs text-muted-foreground">{userEmail}</p>}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onSignOut}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('header.logOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}
